import { create } from 'zustand'
import { AxiosResponse } from 'axios'
import { useApi } from '@/stores/useApi.ts'
import { Album, AlbumPreviewFile } from '@/models/album.ts'
import { UploadFile } from '@/stores/useUploadQueue.ts'
import { AlbumFileType } from '@/models/albumFileType.ts'
import { AlbumFileUpload } from '@/models/albumFileUpload.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import { HeyProfile } from '@/models/profile.ts'
import { HeyDrive, HeyDriveType, DriveContact, OpenedImageCaption } from '@/models/drive.ts'
import { DownloadOptions, DownloadQuality } from '@/models/downloadOptions.ts'
import HeyGame from '@/models/heyGame.ts'
import { FullDownload } from '@/models/fullDownload.ts'
import { FileFilterOptions, getURLParamsForFilter } from '@/contexts/fileFilter.tsx'
import { AlbumFileLike } from '@/models/albumFileLike.ts'
import { AlbumFileComment } from '@/models/albumFileComment.ts'
import OrderItemKind from '@/models/orderItemKind.ts'
import OrderItem from '@/models/orderItem.ts'
import Playlist from '@/models/playlist.ts'
import PlaylistFileConnection from '@/models/playlistFileConnection.ts'
import PrintDelivery from '@/models/printDelivery.ts'
import AlbumGuestbookEntry from '@/models/albumGuestbookEntry.ts'
import { AddToBasketRequest, UpdateBasketItemRequest, CheckoutRequest } from '@/models/shop.ts'
import { PortalMineResponse, ProfAccount } from '@/models/profAccount.ts'
import {
  ProfileLoginRequest,
  ProfileLoginResponse,
  SetPasswordRequest,
  SetPasswordResponse,
} from '@/models/auth.ts'
import { ProfProject } from '@/models/profProject.ts'
import { ProfTag } from '@/models/profTag.ts'
import { ProfMailTemplate, CreateUpdateMailTemplateRequest } from '@/models/profMailTemplate.ts'
import {
  ProfProjectTemplate,
  CreateUpdateProjectTemplateRequest,
  CaptureProjectTemplateRequest,
  ApplyProjectTemplateResponse,
} from '@/models/profProjectTemplate.ts'
import { UpdateProfAccountRequest } from '@/models/billingAddress.ts'
import { ProfContact, CreateUpdateProfContactRequest } from '@/models/profContact.ts'
import {
  CustomPriceList,
  CustomPriceListSummary,
  CreatePriceListRequest,
  UpdatePriceListRequest,
  UpdatePriceListItemRequest,
} from '@/models/priceList.ts'
import { ProfCommission, ProfPayout } from '@/models/profCommission.ts'
import {
  ProfPortfolio,
  CreateUpdatePortfolioRequest,
  PortfolioViewResponse,
  PortfolioProjectDetailResponse,
  PortfolioAlbum,
} from '@/models/profPortfolio.ts'
import {
  ProfSubscriptionResponse,
  SubscribeRequest,
  SubscribeResponse,
  ChangeSubscriptionRequest,
  ChangeSubscriptionResponse,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  PreviewPriceRequest,
  PreviewPriceResponse,
  UpcomingInvoiceResponse,
  InvoicesResponse,
  SubscriptionPlansResponse,
} from '@/models/profSubscription.ts'
import { ProfStorageResponse, ProfStoragePendingDeletionResponse } from '@/models/profStorage.ts'
import { LayoutMode } from '@/components/Files/FilesListing.tsx'
import { HeyDriveWatermark, CreateWatermarkRequest, UpdateWatermarkRequest } from '@/models/heyDriveWatermark.ts'
import {
  StatEventReport,
  StatReportResponse,
  StatFilters,
  StatInterval,
  StatGroupKey,
  StatSummaryResponse,
  StatTimeseriesResponse,
  StatHistoryEntry,
} from '@/models/stats.ts'

export type MultipleDownloadStatus = 'todo' | 'in_progress' | 'failed' | 'done';

export interface ShareAlbumSelection {
  album_id: string;
  include_all_files: boolean;
  file_ids?: string[];
}

export interface CreateShareRequest {
  title?: string;
  expires_at?: string;
  albums: ShareAlbumSelection[];
}

export interface CreateShareResponse {
  id: string;
}

export interface ShareResponse {
  id: string;
  title: string;
  drive_title: string;
  drive_type: string;
  expires_at: string | null;
  user_defined_expires_at: string | null;
  created_at: string;
  theme_name: string | null
  spacing: number | null
  layout_mode: LayoutMode | null
  album_overview: 'both' | 'desktop' | 'mobile' | 'none' | null
  album_overview_style: 'rect' | 'circle' | null
  are_elements_contained: boolean | null
  contact: DriveContact | null
  // Optional: older backends don't send it — undefined then falls back to the default caption.
  opened_image_caption?: OpenedImageCaption | null
  // Optional: older backends don't send it — undefined then means "all resolutions".
  download_options?: DownloadOptions | null
}

export interface ShareAlbum {
  id: string;
  album_id: string;
  title: string;
  description: string;
  thumbnail: string | null;
  include_all_files: boolean;
  files_count: number;
  preview_desktop: AlbumPreviewFile | null;
  preview_mobile: AlbumPreviewFile | null;
  preview_desktop_center_x: number;
  preview_desktop_center_y: number;
  preview_mobile_center_x: number;
  preview_mobile_center_y: number;
}

export interface SelectionAlbumSelection {
  album_id: string;
  include_all_files: boolean;
  file_ids?: string[];
}

export interface CreateSelectionRequest {
  title?: string;
  name?: string;
  email?: string;
  message?: string;
  albums: SelectionAlbumSelection[];
}

export interface CreateSelectionResponse {
  id: string;
}

export interface SelectionResponse {
  id: string;
  title: string;
  name: string;
  email: string;
  message: string;
  drive_title: string;
  drive_type: string;
  created_at: string;
  theme_name: string | null;
  spacing: number | null;
  layout_mode: LayoutMode | null;
  album_overview: 'both' | 'desktop' | 'mobile' | 'none' | null;
  album_overview_style: 'rect' | 'circle' | null;
  are_elements_contained: boolean | null;
  contact: DriveContact | null;
  // Optional: older backends don't send it — undefined then means "all resolutions".
  download_options?: DownloadOptions | null;
}

export interface SelectionSummary {
  id: string;
  title: string;
  name: string;
  email: string;
  message: string;
  files_count: number;
  created_at: string;
  selection_albums: Array<{
    id: string;
    album: string;
    include_all_files: boolean;
  }>;
}

export interface SelectionAlbum {
  id: string;
  album_id: string;
  title: string;
  description: string;
  thumbnail: string | null;
  include_all_files: boolean;
  files_count: number;
  preview_desktop: AlbumPreviewFile | null;
  preview_mobile: AlbumPreviewFile | null;
  preview_desktop_center_x: number;
  preview_desktop_center_y: number;
  preview_mobile_center_x: number;
  preview_mobile_center_y: number;
}

export interface MultipleDownloadCreateResponse {
  id: string;
  status: MultipleDownloadStatus;
  projected_size: number;
  files_count: number;
  /** 0.0 – 1.0, always 0 on creation. Older backends omit it — treat as 0. */
  progress: number;
}

// Response for GET /api/drive/{id}/download_multiple_status/?request_id=...
export interface MultipleDownloadStatusResponse {
  id: string;
  status: MultipleDownloadStatus;
  projected_size: number;
  files_count: number;
  result_size: number | null;
  download_url: string | null;
  error_message: string | null;
  /**
   * 0.0 – 1.0. Arrives in few, large steps: up to 0.9 while the files are fetched
   * from storage (measured in bytes), 0.95 once the ZIP is packed and the upload
   * runs, 1.0 together with `status === 'done'`. Never a completion signal on its
   * own — only `status === 'done'` plus `download_url` is.
   */
  progress: number;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}


interface UseClientActions {
  createGuestProfile: (displayName: string, album: Album) => Promise<AxiosResponse>
  createFileUpload: (album: Album, uploadFile: UploadFile, game?: HeyGame, guestbookEntry?: AlbumGuestbookEntry) => Promise<AxiosResponse>
  completeFileUpload: (fileUpload: AlbumFileUpload) => Promise<AxiosResponse>
  completeFileMultipartUpload: (fileUpload: AlbumFileUpload, etags: Array<{part_number: number, etag: string}>) => Promise<AxiosResponse>
  getFiles: (album: Album, filter?: Record<string, string> | FileFilterOptions) => Promise<AxiosResponse>
  getPreviewExamples: (driveId: string) => Promise<AxiosResponse>
  getGuestbookEntries: (albumId: string, filter?: Record<string, string> | FileFilterOptions) => Promise<AxiosResponse>
  getFullDownloads: (drive: HeyDrive) => Promise<AxiosResponse>
  getFullDownloadRequests: (drive: HeyDrive) => Promise<AxiosResponse>
  requestFullDownload: (drive: HeyDrive) => Promise<AxiosResponse>
  requestMultiDownload: (driveId: string, albumIds: Array<string>, fileIds: Array<string>, quality: DownloadQuality) => Promise<AxiosResponse<MultipleDownloadCreateResponse>>
  getMultiDownload: (driveId: string, multiId: string) => Promise<AxiosResponse<MultipleDownloadStatusResponse>>
  deleteFullDownload: (drive: HeyDrive, download: FullDownload) => Promise<AxiosResponse>
  deleteFiles: (album: Album, filter: Array<Array<string>>) => Promise<AxiosResponse>
  updateFiles: (album: Album, filter: Array<Array<string>>, data: any) => Promise<AxiosResponse>
  getLiveshow: (album: Album, filter?: Record<string, string>) => Promise<AxiosResponse>
  deleteFile: (file: AlbumFile) => Promise<AxiosResponse>
  deleteAlbumGuestbookEntry: (guestbookEntry: AlbumGuestbookEntry) => Promise<AxiosResponse>
  updateProfile: (username: string, data: Partial<HeyProfile>) => Promise<AxiosResponse>
  updateDrive: (driveId: string, data: Partial<HeyDrive> | FormData) => Promise<AxiosResponse>
  updateQrCode: (driveId: string, data: Partial<HeyDrive> | FormData) => Promise<AxiosResponse>
  updateAlbum: (albumId: string, data: Partial<Album> | FormData) => Promise<AxiosResponse>
  deleteAlbum: (albumId: string) => Promise<AxiosResponse>
  reorderAlbums: (driveId: string, albumIds: string[]) => Promise<AxiosResponse<Album[]>>
  createAlbum: (driveId: string, data: Partial<Album> | FormData) => Promise<AxiosResponse>
  applyFileDeclaration: (albumId: string, declarationName: string) => Promise<AxiosResponse<Album>>
  createGame: (album: Album, challenge: string) => Promise<AxiosResponse>
  createGuestbookEntry: (album: Album, content: string, audio: File | Blob | null) => Promise<AxiosResponse>
  updateGame: (gameId: string, challenge: string) => Promise<AxiosResponse>
  deleteGame: (gameId: string) => Promise<AxiosResponse>
  createDemo: (email: string, driveType?: HeyDriveType, country?: string, language?: string, eventDate?: string) => Promise<AxiosResponse>
  createProfDemo: (email: string, country?: string, language?: string) => Promise<AxiosResponse>
  // --- Auth (profile level, generic for PicField + FridaySnap) ---
  login: (data: ProfileLoginRequest) => Promise<AxiosResponse<ProfileLoginResponse>>
  setPassword: (data: SetPasswordRequest) => Promise<AxiosResponse<SetPasswordResponse>>
  activateDrive: (email: string, activationCode: string, driveType?: HeyDriveType, country?: string, language?: string) => Promise<AxiosResponse>
  sendCoupon: (driveId: string, email: string) => Promise<AxiosResponse>
  like: (albumFile: AlbumFile) => Promise<AxiosResponse>
  unlike: (albumFile: AlbumFile, like: AlbumFileLike) => Promise<AxiosResponse>
  comment: (albumFile: AlbumFile, content: string) => Promise<AxiosResponse>
  fileDescription: (albumFile: AlbumFile, description: string) => Promise<AxiosResponse>
  deleteComment: (albumFile: AlbumFile, comment: AlbumFileComment) => Promise<AxiosResponse>
  createPlaylist: (drive: HeyDrive, kind: OrderItemKind) => Promise<AxiosResponse>
  getPlaylists: (drive: HeyDrive) => Promise<AxiosResponse>
  initGalleryUpgrade: (drive: HeyDrive) => Promise<AxiosResponse>
  initStorageUpgrade: (drive: HeyDrive, months: number) => Promise<AxiosResponse>
  createOrderItem: (drive: HeyDrive, options: Partial<OrderItem>) => Promise<AxiosResponse>
  getOrderItems: (drive: HeyDrive) => Promise<AxiosResponse>
  addPlaylistFiles: (playlist: Playlist, filter: Array<Array<string>>) => Promise<AxiosResponse>
  removePlaylistFiles: (playlist: Playlist, filter: Array<PlaylistFileConnection>) => Promise<AxiosResponse>
  buyOrderItem: (orderItem: OrderItem) => Promise<AxiosResponse>
  getPrintDelivery: (id: string) => Promise<AxiosResponse>
  confirmPrintDelivery: (deliveryId: string) => Promise<AxiosResponse>
  uploadPrintDeliveryFile: (deliveryId: string, file: File) => Promise<AxiosResponse>
  deletePrintDeliveryFile: (fileId: string) => Promise<AxiosResponse>
  updatePrintDelivery: (deliveryId: string, delivery: Partial<PrintDelivery>) => Promise<AxiosResponse>
  getFullDownloadUrl: (fullDownload: FullDownload, durationHours: number) => Promise<AxiosResponse>
  getIncreaseNotes: (driveId: string) => Promise<AxiosResponse>
  getPaymentStatus: (driveId: string, sessionId: string) => Promise<AxiosResponse>
  getProducts: (driveId: string, isPrintProduct?: boolean) => Promise<AxiosResponse>
  getProduct: (productId: string) => Promise<AxiosResponse>
  getCurrentBasket: (driveId: string) => Promise<AxiosResponse>
  addToBasket: (item: AddToBasketRequest & { files?: string[], album_ids?: string[], pages?: Array<Record<string, any>>, crop_data?: Record<string, any> }) => Promise<AxiosResponse>
  updateBasketItem: (request: UpdateBasketItemRequest) => Promise<AxiosResponse>
  removeBasketItem: (itemId: string) => Promise<AxiosResponse>
  clearBasket: () => Promise<AxiosResponse>
  checkout: (checkoutData: CheckoutRequest) => Promise<AxiosResponse>
  getOrders: () => Promise<AxiosResponse>
  getOrder: (orderId: string) => Promise<AxiosResponse>
  cancelOrder: (orderId: string) => Promise<AxiosResponse>
  updateBasketItemDetails: (basketItemId: string, data: { files?: string[], crop_data?: Record<string, any>, update_existing?: boolean, page_datas?: Array<any>, connection_datas?: Array<any> }) => Promise<AxiosResponse>
  getBasketItemDetail: (basketItemId: string) => Promise<AxiosResponse>
  createShare: (driveId: string, data: CreateShareRequest) => Promise<AxiosResponse<CreateShareResponse>>
  getShare: (shareId: string) => Promise<AxiosResponse<ShareResponse>>
  getShareAlbums: (shareId: string) => Promise<AxiosResponse<ShareAlbum[]>>
  getShareAlbumFiles: (shareId: string, shareAlbumId: string, filter?: Record<string, string>) => Promise<AxiosResponse>
  createSelection: (driveId: string, data: CreateSelectionRequest) => Promise<AxiosResponse<CreateSelectionResponse>>
  getDriveSelections: (driveId: string) => Promise<AxiosResponse<SelectionSummary[]>>
  deleteSelection: (driveId: string, selectionId: string) => Promise<AxiosResponse>
  removeSelectionAlbum: (driveId: string, selectionId: string, selectionAlbumId: string) => Promise<AxiosResponse>
  removeSelectionAlbumFile: (driveId: string, selectionId: string, selectionAlbumId: string, fileId: string) => Promise<AxiosResponse>
  getSelection: (selectionId: string) => Promise<AxiosResponse<SelectionResponse>>
  getSelectionAlbums: (selectionId: string) => Promise<AxiosResponse<SelectionAlbum[]>>
  getSelectionAlbumFiles: (selectionId: string, selectionAlbumId: string, filter?: Record<string, string>) => Promise<AxiosResponse>
  downloadSelection: (selectionId: string, quality?: DownloadQuality) => Promise<AxiosResponse<MultipleDownloadCreateResponse>>
  getPortalMine: () => Promise<AxiosResponse<PortalMineResponse>>
  updatePortalMine: (data: UpdateProfAccountRequest) => Promise<AxiosResponse<ProfAccount>>
  getProjects: (params?: { page?: number, page_size?: number, search?: string, o?: string, tag?: string }) => Promise<AxiosResponse<PaginatedResponse<ProfProject>>>
  getProject: (projectId: string) => Promise<AxiosResponse<ProfProject>>
  createProject: (title: string, templateId?: string | null) => Promise<AxiosResponse<ProfProject>>
  deleteProject: (projectId: string) => Promise<AxiosResponse>
  updateProject: (projectId: string, data: { title?: string, price_list?: string | null, tag_ids?: string[], note?: string, template?: string | null, contact?: string | null }) => Promise<AxiosResponse<ProfProject>>
  sendProjectMail: (projectId: string, data: { email: string, subject: string, message: string, send_copy: boolean }) => Promise<AxiosResponse>
  getMailTemplates: () => Promise<AxiosResponse<ProfMailTemplate[]>>
  getMailTemplate: (id: string) => Promise<AxiosResponse<ProfMailTemplate>>
  createMailTemplate: (data: CreateUpdateMailTemplateRequest) => Promise<AxiosResponse<ProfMailTemplate>>
  updateMailTemplate: (id: string, data: Partial<CreateUpdateMailTemplateRequest>) => Promise<AxiosResponse<ProfMailTemplate>>
  deleteMailTemplate: (id: string) => Promise<AxiosResponse>
  getProjectTemplates: () => Promise<AxiosResponse<ProfProjectTemplate[]>>
  getProjectTemplate: (id: string) => Promise<AxiosResponse<ProfProjectTemplate>>
  createProjectTemplate: (data: CreateUpdateProjectTemplateRequest) => Promise<AxiosResponse<ProfProjectTemplate>>
  createProjectTemplateFromProject: (data: CaptureProjectTemplateRequest) => Promise<AxiosResponse<ProfProjectTemplate>>
  updateProjectTemplate: (id: string, data: CreateUpdateProjectTemplateRequest) => Promise<AxiosResponse<ProfProjectTemplate>>
  deleteProjectTemplate: (id: string) => Promise<AxiosResponse>
  captureProjectTemplate: (id: string, data: CaptureProjectTemplateRequest) => Promise<AxiosResponse<ProfProjectTemplate>>
  applyProjectTemplate: (id: string, projectIds?: string[]) => Promise<AxiosResponse<ApplyProjectTemplateResponse>>
  duplicateProjectTemplate: (id: string) => Promise<AxiosResponse<ProfProjectTemplate>>
  getContacts: () => Promise<AxiosResponse<ProfContact[]>>
  getContact: (id: string) => Promise<AxiosResponse<ProfContact>>
  createContact: (data: CreateUpdateProfContactRequest) => Promise<AxiosResponse<ProfContact>>
  updateContact: (id: string, data: CreateUpdateProfContactRequest) => Promise<AxiosResponse<ProfContact>>
  deleteContact: (id: string) => Promise<AxiosResponse>
  getSubscription: () => Promise<AxiosResponse<ProfSubscriptionResponse>>
  subscribe: (data: SubscribeRequest) => Promise<AxiosResponse<SubscribeResponse>>
  changeSubscription: (data: ChangeSubscriptionRequest) => Promise<AxiosResponse<ChangeSubscriptionResponse>>
  cancelSubscription: () => Promise<AxiosResponse<CancelSubscriptionResponse>>
  reactivateSubscription: () => Promise<AxiosResponse<ReactivateSubscriptionResponse>>
  previewSubscriptionPrice: (data: PreviewPriceRequest) => Promise<AxiosResponse<PreviewPriceResponse>>
  getUpcomingInvoice: () => Promise<AxiosResponse<UpcomingInvoiceResponse>>
  getInvoices: (params?: { limit?: number, starting_after?: string }) => Promise<AxiosResponse<InvoicesResponse>>
  getSubscriptionPlans: () => Promise<AxiosResponse<SubscriptionPlansResponse>>
  getStorage: () => Promise<AxiosResponse<ProfStorageResponse>>
  getStoragePendingDeletion: () => Promise<AxiosResponse<ProfStoragePendingDeletionResponse>>
  getPriceLists: () => Promise<AxiosResponse<CustomPriceListSummary[]>>
  createPriceList: (data: CreatePriceListRequest) => Promise<AxiosResponse<CustomPriceList>>
  getPriceList: (id: string) => Promise<AxiosResponse<CustomPriceList>>
  updatePriceList: (id: string, data: UpdatePriceListRequest) => Promise<AxiosResponse<CustomPriceList>>
  deletePriceList: (id: string) => Promise<AxiosResponse>
  updatePriceListItem: (listId: string, itemId: string, data: UpdatePriceListItemRequest) => Promise<AxiosResponse>
  getCommissions: (params?: { page?: number }) => Promise<AxiosResponse<PaginatedResponse<ProfCommission>>>
  getCommission: (id: string) => Promise<AxiosResponse<ProfCommission>>
  getPayouts: (params?: { page?: number }) => Promise<AxiosResponse<PaginatedResponse<ProfPayout>>>
  getPayout: (id: string) => Promise<AxiosResponse<ProfPayout>>
  createTag: (data: { name: string, color?: string }) => Promise<AxiosResponse<ProfTag>>
  updateTag: (id: string, data: { name?: string, color?: string }) => Promise<AxiosResponse<ProfTag>>
  deleteTag: (id: string) => Promise<AxiosResponse>
  getPortfolios: (params?: { page?: number, page_size?: number, search?: string, o?: string }) => Promise<AxiosResponse<ProfPortfolio[] | PaginatedResponse<ProfPortfolio>>>
  getPortfolio: (id: string) => Promise<AxiosResponse<ProfPortfolio>>
  createPortfolio: (title: string) => Promise<AxiosResponse<ProfPortfolio>>
  updatePortfolio: (id: string, data: CreateUpdatePortfolioRequest | FormData) => Promise<AxiosResponse<ProfPortfolio>>
  deletePortfolio: (id: string) => Promise<AxiosResponse>
  duplicatePortfolio: (id: string) => Promise<AxiosResponse<ProfPortfolio>>
  getPortfolioView: (portfolioId: string) => Promise<AxiosResponse<PortfolioViewResponse>>
  getPortfolioProject: (portfolioId: string, projectId: string) => Promise<AxiosResponse<PortfolioProjectDetailResponse>>
  getPortfolioAlbums: (portfolioId: string, projectId: string) => Promise<AxiosResponse<PortfolioAlbum[]>>
  getPortfolioAlbumFiles: (portfolioId: string, projectId: string, albumId: string, filter?: Record<string, string>) => Promise<AxiosResponse>
  getWatermarks: () => Promise<AxiosResponse<HeyDriveWatermark[]>>
  getWatermark: (watermarkId: string) => Promise<AxiosResponse<HeyDriveWatermark>>
  createWatermark: (data: CreateWatermarkRequest) => Promise<AxiosResponse<HeyDriveWatermark>>
  updateWatermark: (watermarkId: string, data: UpdateWatermarkRequest) => Promise<AxiosResponse<HeyDriveWatermark>>
  deleteWatermark: (watermarkId: string) => Promise<AxiosResponse>
  activateDriveWatermark: (driveId: string, watermarkId: string | null) => Promise<AxiosResponse<HeyDrive>>
  reportStats: (events: StatEventReport | StatEventReport[]) => Promise<AxiosResponse<StatReportResponse>>
  getStatsSummary: (filters?: StatFilters) => Promise<AxiosResponse<StatSummaryResponse>>
  getStatsTimeseries: (filters?: StatFilters & { interval?: StatInterval, group?: StatGroupKey[] }) => Promise<AxiosResponse<StatTimeseriesResponse>>
  getStatsHistory: (filters?: StatFilters & { page?: number, page_size?: number }) => Promise<AxiosResponse<PaginatedResponse<StatHistoryEntry>>>
}

// Takes any plain object (interfaces like StatFilters have no index signature,
// so Record<string, unknown> would reject them).
function statParams(filters?: object): string {
  if (!filters) return ''
  const entries = Object.entries(filters)
    .filter(([, value]) => value != null && value !== '')
    .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : String(value)] as [string, string])
  return entries.length ? '?' + new URLSearchParams(entries).toString() : ''
}

// FormData only when an image is uploaded, JSON otherwise
function contactPayload(data: CreateUpdateProfContactRequest): CreateUpdateProfContactRequest | FormData {
  if (!(data.contact_image instanceof File)) return data
  const formData = new FormData()
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue
    formData.append(key, value instanceof File ? value : String(value))
  }
  return formData
}

export const useClient = create<UseClientActions>((_) => ({
  createGuestProfile(displayName: string, album: Album) {
    return useApi.getState().post('api/profile/createGuest', {
      display_name: displayName,
      album: album.id,
    })
  },
  createFileUpload(album: Album, uploadFile: UploadFile, game = undefined, guestbookEntry = undefined) {
    const mimeType = uploadFile.file.type || null
    if (mimeType == null) {
      throw 'no_mime_type'
    }
    if (!(mimeType.startsWith('image') || mimeType.startsWith('video'))) {
      throw 'invalid_mime_type'
    }
    return useApi.getState().post(`api/album/${album.id}/upload/`, {
      file_name: uploadFile.file.name,
      file_hash: uploadFile.fileHash,
      game: game ? game.id : null,
      guestbook_entry: guestbookEntry ? guestbookEntry.id : null,
      content_type: mimeType,
      size: uploadFile.file.size,
      file_creation: new Date(uploadFile.file.lastModified),
      file_type: mimeType.startsWith('image') ? AlbumFileType.IMAGE : AlbumFileType.VIDEO,
    })
  },
  requestMultiDownload(driveId: string, albumIds: Array<string>, fileIds: Array<string>, quality: DownloadQuality) {
    return useApi.getState().post(`api/drive/${driveId}/download_multiple/`, {
      album_ids: albumIds,
      file_ids: fileIds,
      quality,
    })
  },
  completeFileUpload(fileUpload: AlbumFileUpload) {
    return useApi.getState().post(`api/uploads/${fileUpload.id}/upload_complete/`, {})
  },
  deleteFullDownload(drive: HeyDrive, download: FullDownload) {
    return useApi.getState().delete(`api/drive/${drive.id}/full_download/?id=${download.id}`)
  },
  requestFullDownload(drive: HeyDrive) {
    return useApi.getState().post(`api/drive/${drive.id}/create_full_download_request/`, {})
  },
  completeFileMultipartUpload(fileUpload: AlbumFileUpload, etags: Array<{part_number: number, etag: string}>) {
    return useApi.getState().post(`api/uploads/${fileUpload.id}/upload_complete/`, {
      etags,
    })
  },
  getFullDownloads(drive: HeyDrive): Promise<AxiosResponse>{
    return useApi.getState().get(`api/drive/${drive.id}/full_downloads/`)
  },
  getMultiDownload(driveId: string, multiId: string): Promise<AxiosResponse>{
    return useApi.getState().get(`api/drive/${driveId}/download_multiple_status/?request_id=${multiId}`)
  },

  getFullDownloadRequests(drive: HeyDrive): Promise<AxiosResponse>{
    return useApi.getState().get(`api/drive/${drive.id}/full_download_requests/`)
  },
  getFiles(album: Album, filter = undefined) {
    let urlParams: string | undefined
    if (filter) {
      if (filter.sort || filter.media) {
        const params = getURLParamsForFilter(filter)
        urlParams = new URLSearchParams(params).toString()
      } else {
        urlParams = new URLSearchParams(filter as Record<string, string>).toString()
      }
    }
    return useApi.getState().get(`api/album/${album.id}/files/` + (urlParams ? '?' + urlParams : ''))
  },
  getPreviewExamples(driveId: string): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${driveId}/preview_examples/`)
  },
  getGuestbookEntries(albumId: string, filter = undefined) {
    let urlParams: string | undefined
    if (filter) {
      if (filter.sort) {
        const params = getURLParamsForFilter(filter)
        urlParams = new URLSearchParams(params).toString()
      } else {
        urlParams = new URLSearchParams(filter as Record<string, string>).toString()
      }
    }
    return useApi.getState().get(`api/album/${albumId}/guestbook_entries/` + (urlParams ? '?' + urlParams : ''))
  },
  deleteFiles(album: Album, filter) {
    return useApi.getState().delete(`api/album/${album.id}/files/?` + new URLSearchParams(filter).toString(), { timeout: 100000 })
  },
  updateFiles(album: Album, filter, data) {
    return useApi.getState().patch(`api/album/${album.id}/files/?` + new URLSearchParams(filter).toString(), data)
  },
  deleteFile(file: AlbumFile) {
    return useApi.getState().delete(`api/files/${file.id}/`)
  },
  deleteAlbumGuestbookEntry(guestbookEntry: AlbumGuestbookEntry) {
    return useApi.getState().delete(`api/guestbook_entries/${guestbookEntry.id}/`)
  },
  updateProfile(username: string, data: Partial<HeyProfile>) {
    return useApi.getState().patch(`api/profile/${username}/`, data)
  },
  updateDrive(driveId: string, data: Partial<HeyDrive> | FormData) {
    return useApi.getState().patch(`api/drive/${driveId}/`, data)
  },
  updateQrCode(driveId: string, data: Partial<HeyDrive> | FormData) {
    return useApi.getState().patch(`api/drive/${driveId}/update_qr_code/`, data)
  },
  updateAlbum(albumId: string, data: Partial<Album> | FormData) {
    return useApi.getState().patch(`api/album/${albumId}/`, data)
  },
  createAlbum(driveId: string, data: Partial<Album> | FormData) {
    return useApi.getState().post(`api/drive/${driveId}/create_album/`, data)
  },
  applyFileDeclaration(albumId: string, declarationName: string) {
    return useApi.getState().post(`api/album/${albumId}/apply_file_declaration/`, {
      declaration_name: declarationName,
    })
  },
  createGame(album: Album, challenge: string) {
    return useApi.getState().post(`api/album/${album.id}/create_game/`, {
      challenge,
    })
  },
  createGuestbookEntry(album: Album, content: string, audio: File | Blob | null) {
    const data = new FormData()
    data.append('content', content)
    if (audio) {
      data.append('audio', audio)
    }
    return useApi.getState().post(`api/album/${album.id}/create_guestbook_entry/`, data)
  },
  updateGame(gameId: string, challenge: string) {
    return useApi.getState().patch(`api/game/${gameId}/`, {
      challenge,
    })
  },
  deleteGame(gameId: string) {
    return useApi.getState().delete(`api/game/${gameId}/`)
  },
  createDemo(email: string, driveType?: HeyDriveType, country?: string, language?: string, eventDate?: string) {
    const demoData: Record<string, string> = { email }
    if (driveType) {
      demoData['drive_type'] = driveType
    }
    if (country) {
      demoData['c'] = country
    }
    if (language) {
      demoData['l'] = language
    }
    if (eventDate) {
      demoData['event_date'] = eventDate
    }
    return useApi.getState().post(`api/demo/`, demoData)
  },
  createProfDemo(email: string, country?: string, language?: string)  {
    const demoData: Record<string, string> = { email }
    if (country) {
      demoData['c'] = country
    }
    if (language) {
      demoData['l'] = language
    }
    return useApi.getState().post(`api/prof/demo/`, demoData)
  },
  /**
   * One call that decides: logs in with the password, asks for the password field,
   * or sends the login link. The outcome is in `result`.
   * Never creates an account — on 404 `unknown_email` move on to createProfDemo /
   * createDemo.
   * On `result === 'logged_in'` the token comes in the `HA-Token` response header.
   *
   * No trailing slash, exactly like `api/profile/mine` — with one the POST goes nowhere.
   */
  login(data: ProfileLoginRequest) {
    return useApi.getState().post('api/profile/login', data)
  },
  /**
   * Set or change the password. Only send `current_password` when
   * profile.has_password is already true.
   * The auth token does NOT change — nothing needs to be stored again.
   *
   * No trailing slash, see `login`.
   */
  setPassword(data: SetPasswordRequest) {
    return useApi.getState().post('api/profile/password', data)
  },
  activateDrive(email: string, activationCode: string, driveType?: HeyDriveType, country?: string, language?: string) {
    const demoData: Record<string, string> = { email, code: activationCode }
    if (driveType) {
      demoData['drive_type'] = driveType
    }
    if (country) {
      demoData['c'] = country
    }
    if (language) {
      demoData['l'] = language
    }
    return useApi.getState().post(`api/activate/`, demoData)
  },
  getLiveshow(album: Album, filter?: Record<string, string>) {
    return useApi.getState().get(`api/album/${album.id}/liveshow/` + (filter ? '?' + new URLSearchParams(filter).toString() : ''))
  },
  deleteAlbum(albumId: string) {
    return useApi.getState().delete(`api/album/${albumId}/`)
  },
  /**
   * Sets the album order of a drive. Send ALL album ids of the drive in the
   * desired order (ids that are left out keep their order and are appended).
   * The album at index 0 becomes the primary album (`is_primary`), every other
   * album loses the flag – this is the only way to change the primary album.
   * Returns the albums in their new order with `order` and `is_primary` updated.
   */
  reorderAlbums(driveId: string, albumIds: string[]) {
    return useApi.getState().post(`api/drive/${driveId}/reorder_albums/`, { album_ids: albumIds })
  },
  sendCoupon(driveId: string, email: string) {
    return useApi.getState().post(`api/drive_coupon/`, { email, drive: driveId })
  },
  like(albumFile: AlbumFile): Promise<AxiosResponse> {
    return useApi.getState().post(`api/files/${albumFile.id}/like/`, {})
  },
  unlike(albumFile: AlbumFile, like: AlbumFileLike): Promise<AxiosResponse> {
    return useApi.getState().post(`api/files/${albumFile.id}/unlike/`, {
      id: like.id,
    })
  },
  comment(albumFile: AlbumFile, content: string): Promise<AxiosResponse> {
    return useApi.getState().post(`api/files/${albumFile.id}/comment/`, {
      content,
    })
  },
  fileDescription(albumFile: AlbumFile, description: string): Promise<AxiosResponse> {
    return useApi.getState().post(`api/files/${albumFile.id}/update_description/`, {
      description,
    })
  },
  deleteComment(albumFile: AlbumFile, comment: AlbumFileComment): Promise<AxiosResponse> {
    return useApi.getState().post(`api/files/${albumFile.id}/delete_comment/`, {
      id: comment.id,
    })
  },
  createPlaylist(drive: HeyDrive, kind: OrderItemKind): Promise<AxiosResponse> {
    return useApi.getState().post(`api/drive/${drive.id}/create_playlist/`, {
      kind,
    })
  },
  getPlaylists(drive: HeyDrive): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${drive.id}/playlists/`)
  },
  initGalleryUpgrade(drive: HeyDrive): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${drive.id}/init_upgrade/`)
  },
  initStorageUpgrade(drive: HeyDrive, months: number): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${drive.id}/init_storage_increase/?months=${months}`)
  },
  createOrderItem(drive: HeyDrive, options: Partial<OrderItem>): Promise<AxiosResponse> {
    return useApi.getState().post(`api/drive/${drive.id}/create_order_item/`, {
      ...options,
    })
  },
  getOrderItems(drive: HeyDrive): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${drive.id}/order_items/`)
  },
  addPlaylistFiles(playlist: Playlist, filter: Array<Array<string>>): Promise<AxiosResponse> {
    return useApi.getState().post(`api/playlist/${playlist.id}/add_files/` + (filter ? '?' + new URLSearchParams(filter).toString() : ''), {})
  },
  removePlaylistFiles(playlist: Playlist, filter: Array<PlaylistFileConnection>): Promise<AxiosResponse> {
    const ids = filter.map(connection => connection.id)
    return useApi.getState().delete(`api/playlist/${playlist.id}/remove_files/` + (filter ? '?' + new URLSearchParams({ids: ids.join(',')}).toString() : ''), {})
  },
  buyOrderItem(orderItem: OrderItem): Promise<AxiosResponse> {
    return useApi.getState().post(`api/order/${orderItem.id}/buy/`, {})
  },
  getPrintDelivery(id: string): Promise<AxiosResponse> {
    return useApi.getState().get(`api/delivery/${id}/`)
  },
  getFullDownloadUrl(fullDownload: FullDownload, durationHours: number): Promise<AxiosResponse> {
    return useApi.getState().get(`api/download/${fullDownload.id}/get_download_url/?duration=${durationHours}`)
  },
  getIncreaseNotes(driveId: string): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${driveId}/storage_increases/`)
  },
  getPaymentStatus(driveId: string, sessionId: string): Promise<AxiosResponse> {
    return useApi.getState().get(`api/drive/${driveId}/payment_status/?session_id=${sessionId}`)
  },
  confirmPrintDelivery(deliveryId: string): Promise<AxiosResponse> {
    return useApi.getState().post(`api/delivery/${deliveryId}/confirm/`, {})
  },
  uploadPrintDeliveryFile(deliveryId: string, file: File): Promise<AxiosResponse> {
    const data = new FormData()
    data.append('file', file)
    return useApi.getState().post(`api/delivery/${deliveryId}/upload_file/`, data)
  },
  deletePrintDeliveryFile(fileId: string): Promise<AxiosResponse> {
    return useApi.getState().delete(`api/delivery_file/${fileId}/`)
  },
  updatePrintDelivery(deliveryId: string, delivery: Partial<PrintDelivery>): Promise<AxiosResponse> {
    return useApi.getState().patch(`api/delivery/${deliveryId}/`, delivery)
  },
  getProducts(driveId: string, isPrintProduct?: boolean) {
    const params = `?drive_id=${driveId}` + (isPrintProduct !== undefined ? `&is_print_product=${isPrintProduct}` : '')
    return useApi.getState().get(`api/shop/products/${params}`)
  },
  getProduct(productId: string) {
    return useApi.getState().get(`api/shop/products/${productId}/`)
  },
  getCurrentBasket(driveId: string) {
    return useApi.getState().get(`api/shop/baskets/current/?drive_id=${driveId}`)
  },
  addToBasket(item: AddToBasketRequest & { files?: string[], album_ids?: string[], pages?: Array<Record<string, any>>, crop_data?: Record<string, any> }) {
    return useApi.getState().post(`api/shop/baskets/add_item/?drive_id=${item.drive_id}`, item)
  },
  updateBasketItem(request: UpdateBasketItemRequest) {
    return useApi.getState().patch(`api/shop/baskets/update_item/`, request)
  },
  removeBasketItem(itemId: string) {
    return useApi.getState().delete(`api/shop/baskets/remove_item/?item_id=${itemId}`)
  },
  clearBasket() {
    return useApi.getState().delete(`api/shop/baskets/clear/`)
  },
  checkout(checkoutData: CheckoutRequest) {
    return useApi.getState().post(`api/shop/baskets/create_checkout_session/`, checkoutData)
  },
  getOrders() {
    return useApi.getState().get(`api/shop/orders/`)
  },
  getOrder(orderId: string) {
    return useApi.getState().get(`api/shop/orders/${orderId}/`)
  },
  cancelOrder(orderId: string) {
    return useApi.getState().patch(`api/shop/orders/${orderId}/cancel/`, {})
  },
  updateBasketItemDetails(basketItemId: string, data: { files?: string[], crop_data?: Record<string, any>, update_existing?: boolean, pages_data?: Array<any>, connection_datas?: Array<any> },) {
    return useApi.getState().patch(`api/shop/baskets/update_basket_item/`, {
      item_id: basketItemId,
      ...data
    })
  },
  getBasketItemDetail(basketItemId: string) {
    return useApi.getState().get(`api/shop/baskets/get_basket_item_detail/?item_id=${basketItemId}`)
  },
  createShare(driveId: string, data: CreateShareRequest) {
    return useApi.getState().post(`api/drive/${driveId}/create_share/`, data)
  },
  getShare(shareId: string) {
    return useApi.getState().get(`api/share/${shareId}/`)
  },
  getShareAlbums(shareId: string) {
    return useApi.getState().get(`api/share/${shareId}/albums/`)
  },
  getShareAlbumFiles(shareId: string, shareAlbumId: string, filter?: Record<string, string>) {
    const params = filter ? '?' + new URLSearchParams(filter).toString() : ''
    return useApi.getState().get(`api/share/${shareId}/albums/${shareAlbumId}/files/${params}`)
  },
  createSelection(driveId: string, data: CreateSelectionRequest) {
    return useApi.getState().post(`api/drive/${driveId}/create_selection/`, data)
  },
  getDriveSelections(driveId: string) {
    return useApi.getState().get(`api/drive/${driveId}/selections/`)
  },
  deleteSelection(driveId: string, selectionId: string) {
    return useApi.getState().delete(`api/drive/${driveId}/selections/${selectionId}/`)
  },
  removeSelectionAlbum(driveId: string, selectionId: string, selectionAlbumId: string) {
    return useApi.getState().delete(`api/drive/${driveId}/selections/${selectionId}/albums/${selectionAlbumId}/`)
  },
  removeSelectionAlbumFile(driveId: string, selectionId: string, selectionAlbumId: string, fileId: string) {
    return useApi.getState().delete(`api/drive/${driveId}/selections/${selectionId}/albums/${selectionAlbumId}/files/${fileId}/`)
  },
  getSelection(selectionId: string) {
    return useApi.getState().get(`api/selection/${selectionId}/`)
  },
  getSelectionAlbums(selectionId: string) {
    return useApi.getState().get(`api/selection/${selectionId}/albums/`)
  },
  getSelectionAlbumFiles(selectionId: string, selectionAlbumId: string, filter?: Record<string, string>) {
    const params = filter ? '?' + new URLSearchParams(filter).toString() : ''
    return useApi.getState().get(`api/selection/${selectionId}/albums/${selectionAlbumId}/files/${params}`)
  },
  downloadSelection(selectionId: string, quality: DownloadQuality = 'original') {
    return useApi.getState().post(`api/selection/${selectionId}/download/`, { quality })
  },
  getPortalMine() {
    return useApi.getState().get(`api/prof/mine/`)
  },
  updatePortalMine(data: UpdateProfAccountRequest) {
    // Use FormData when file upload is included
    if (data.contact_image) {
      const formData = new FormData()
      for (const [key, value] of Object.entries(data)) {
        if (value === undefined || value === null) continue
        if (value instanceof File) {
          formData.append(key, value)
        } else {
          formData.append(key, String(value))
        }
      }
      return useApi.getState().patch(`api/prof/mine/`, formData)
    }
    return useApi.getState().patch(`api/prof/mine/`, data)
  },
  getProjects(params = undefined) {
    const urlParams = params ? '?' + new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)]))
    ).toString() : ''
    return useApi.getState().get(`api/prof/projects/${urlParams}`)
  },
  getProject(projectId: string) {
    return useApi.getState().get(`api/prof/projects/${projectId}/`)
  },
  createProject(title: string, templateId?: string | null) {
    return useApi.getState().post('api/prof/projects/', templateId ? { title, template: templateId } : { title })
  },
  deleteProject(projectId: string) {
    return useApi.getState().delete(`api/prof/projects/${projectId}/`)
  },
  updateProject(projectId: string, data: { title?: string, price_list?: string | null, tag_ids?: string[], note?: string, template?: string | null, contact?: string | null }) {
    return useApi.getState().patch(`api/prof/projects/${projectId}/`, data)
  },
  sendProjectMail(projectId: string, data: { email: string, subject: string, message: string, send_copy: boolean }) {
    return useApi.getState().post(`api/prof/projects/${projectId}/send_mail/`, data)
  },
  getMailTemplates() {
    // Templates are account-bound, sorted by creation date (newest first)
    return useApi.getState().get('api/prof/mail-templates/')
  },
  getMailTemplate(id: string) {
    return useApi.getState().get(`api/prof/mail-templates/${id}/`)
  },
  createMailTemplate(data: CreateUpdateMailTemplateRequest) {
    return useApi.getState().post('api/prof/mail-templates/', data)
  },
  updateMailTemplate(id: string, data: Partial<CreateUpdateMailTemplateRequest>) {
    return useApi.getState().patch(`api/prof/mail-templates/${id}/`, data)
  },
  deleteMailTemplate(id: string) {
    return useApi.getState().delete(`api/prof/mail-templates/${id}/`)
  },
  // --- Project templates (account-bound, sorted by name, no pagination) ---
  getProjectTemplates() {
    return useApi.getState().get('api/prof/project-templates/')
  },
  getProjectTemplate(id: string) {
    return useApi.getState().get(`api/prof/project-templates/${id}/`)
  },
  createProjectTemplate(data: CreateUpdateProjectTemplateRequest) {
    return useApi.getState().post('api/prof/project-templates/', data)
  },
  /** Create a template as a snapshot of an existing project (+ its drive) */
  createProjectTemplateFromProject(data: CaptureProjectTemplateRequest) {
    return useApi.getState().post('api/prof/project-templates/', data)
  },
  /** Sending null removes an attribute from the template */
  updateProjectTemplate(id: string, data: CreateUpdateProjectTemplateRequest) {
    return useApi.getState().patch(`api/prof/project-templates/${id}/`, data)
  },
  deleteProjectTemplate(id: string) {
    return useApi.getState().delete(`api/prof/project-templates/${id}/`)
  },
  /** Refill an existing template with the configuration of a project */
  captureProjectTemplate(id: string, data: CaptureProjectTemplateRequest) {
    return useApi.getState().post(`api/prof/project-templates/${id}/capture/`, data)
  },
  /** Without projectIds: applies to ALL projects linked to the template */
  applyProjectTemplate(id: string, projectIds?: string[]) {
    return useApi.getState().post(
      `api/prof/project-templates/${id}/apply/`,
      projectIds ? { project_ids: projectIds } : {},
    )
  },
  duplicateProjectTemplate(id: string) {
    return useApi.getState().post(`api/prof/project-templates/${id}/duplicate/`, {})
  },
  // --- Contacts / "Marken" (account-bound, sorted by name, no pagination) ---
  getContacts() {
    return useApi.getState().get('api/prof/contacts/')
  },
  getContact(id: string) {
    return useApi.getState().get(`api/prof/contacts/${id}/`)
  },
  createContact(data: CreateUpdateProfContactRequest) {
    return useApi.getState().post('api/prof/contacts/', contactPayload(data))
  },
  updateContact(id: string, data: CreateUpdateProfContactRequest) {
    return useApi.getState().patch(`api/prof/contacts/${id}/`, contactPayload(data))
  },
  /** Projects using this contact fall back to the account default contact */
  deleteContact(id: string) {
    return useApi.getState().delete(`api/prof/contacts/${id}/`)
  },
  getSubscription() {
    return useApi.getState().get('api/prof/subscription/')
  },
  subscribe(data: SubscribeRequest) {
    return useApi.getState().post('api/prof/subscription/subscribe/', data)
  },
  changeSubscription(data: ChangeSubscriptionRequest) {
    return useApi.getState().post('api/prof/subscription/change/', data)
  },
  cancelSubscription() {
    return useApi.getState().post('api/prof/subscription/cancel/', {})
  },
  reactivateSubscription() {
    return useApi.getState().post('api/prof/subscription/reactivate/', {})
  },
  previewSubscriptionPrice(data: PreviewPriceRequest) {
    return useApi.getState().post('api/prof/subscription/preview-price/', data)
  },
  getUpcomingInvoice() {
    return useApi.getState().get('api/prof/subscription/upcoming-invoice/')
  },
  getInvoices(params?: { limit?: number, starting_after?: string }) {
    const urlParams = params ? '?' + new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)]))
    ).toString() : ''
    return useApi.getState().get(`api/prof/subscription/invoices/${urlParams}`)
  },
  getSubscriptionPlans() {
    return useApi.getState().get('api/prof/subscription/plans/')
  },
  getStorage() {
    return useApi.getState().get('api/prof/storage/')
  },
  getStoragePendingDeletion() {
    return useApi.getState().get('api/prof/storage/pending-deletion/')
  },
  getPriceLists() {
    return useApi.getState().get('api/prof/price-lists/')
  },
  createPriceList(data: CreatePriceListRequest) {
    return useApi.getState().post('api/prof/price-lists/', data)
  },
  getPriceList(id: string) {
    return useApi.getState().get(`api/prof/price-lists/${id}/`)
  },
  updatePriceList(id: string, data: UpdatePriceListRequest) {
    return useApi.getState().patch(`api/prof/price-lists/${id}/`, data)
  },
  deletePriceList(id: string) {
    return useApi.getState().delete(`api/prof/price-lists/${id}/`)
  },
  updatePriceListItem(listId: string, itemId: string, data: UpdatePriceListItemRequest) {
    return useApi.getState().patch(`api/prof/price-lists/${listId}/items/${itemId}/`, data)
  },
  getCommissions(params = undefined) {
    const urlParams = params ? '?' + new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)]))
    ).toString() : ''
    return useApi.getState().get(`api/prof/commissions/${urlParams}`)
  },
  getCommission(id: string) {
    return useApi.getState().get(`api/prof/commissions/${id}/`)
  },
  getPayouts(params = undefined) {
    const urlParams = params ? '?' + new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)]))
    ).toString() : ''
    return useApi.getState().get(`api/prof/payouts/${urlParams}`)
  },
  getPayout(id: string) {
    return useApi.getState().get(`api/prof/payouts/${id}/`)
  },
  createTag(data: { name: string, color?: string }) {
    return useApi.getState().post('api/prof/tags/', data)
  },
  updateTag(id: string, data: { name?: string, color?: string }) {
    return useApi.getState().patch(`api/prof/tags/${id}/`, data)
  },
  deleteTag(id: string) {
    return useApi.getState().delete(`api/prof/tags/${id}/`)
  },
  getPortfolios(params = undefined) {
    const urlParams = params ? '?' + new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null).map(([k, v]) => [k, String(v)]))
    ).toString() : ''
    return useApi.getState().get(`api/prof/portfolios/${urlParams}`)
  },
  getPortfolio(id: string) {
    return useApi.getState().get(`api/prof/portfolios/${id}/`)
  },
  createPortfolio(title: string) {
    return useApi.getState().post('api/prof/portfolios/', { title })
  },
  updatePortfolio(id: string, data: CreateUpdatePortfolioRequest | FormData) {
    return useApi.getState().patch(`api/prof/portfolios/${id}/`, data)
  },
  deletePortfolio(id: string) {
    return useApi.getState().delete(`api/prof/portfolios/${id}/`)
  },
  duplicatePortfolio(id: string) {
    return useApi.getState().post(`api/prof/portfolios/${id}/duplicate/`, {})
  },
  getPortfolioView(portfolioId: string) {
    return useApi.getState().get(`api/viewPortfolio/${portfolioId}/`)
  },
  getPortfolioProject(portfolioId: string, projectId: string) {
    return useApi.getState().get(`api/viewPortfolio/${portfolioId}/${projectId}/`)
  },
  getPortfolioAlbums(portfolioId: string, projectId: string) {
    return useApi.getState().get(`api/viewPortfolio/${portfolioId}/${projectId}/albums/`)
  },
  getPortfolioAlbumFiles(portfolioId: string, projectId: string, albumId: string, filter?: Record<string, string>) {
    const params = filter ? '?' + new URLSearchParams(filter).toString() : ''
    return useApi.getState().get(`api/viewPortfolio/${portfolioId}/${projectId}/albums/${albumId}/files/${params}`)
  },
  getWatermarks() {
    // Watermarks are account-global; the endpoint returns all watermarks of the account.
    return useApi.getState().get('api/watermarks/')
  },
  getWatermark(watermarkId: string) {
    return useApi.getState().get(`api/watermarks/${watermarkId}/`)
  },
  createWatermark(data: CreateWatermarkRequest) {
    const form = new FormData()
    for (const [k, v] of Object.entries(data)) {
      if (v === undefined || v === null) continue
      if (v instanceof File) form.append(k, v)
      else form.append(k, String(v))
    }
    return useApi.getState().post('api/watermarks/', form)
  },
  updateWatermark(watermarkId: string, data: UpdateWatermarkRequest) {
    const hasFile = data.file instanceof File
    if (hasFile) {
      const form = new FormData()
      for (const [k, v] of Object.entries(data)) {
        if (v === undefined || v === null) continue
        if (v instanceof File) form.append(k, v)
        else form.append(k, String(v))
      }
      return useApi.getState().patch(`api/watermarks/${watermarkId}/`, form)
    }
    return useApi.getState().patch(`api/watermarks/${watermarkId}/`, data)
  },
  deleteWatermark(watermarkId: string) {
    return useApi.getState().delete(`api/watermarks/${watermarkId}/`)
  },
  activateDriveWatermark(driveId: string, watermarkId: string | null) {
    return useApi.getState().patch(`api/drive/${driveId}/`, { watermark: watermarkId })
  },
  // Reporting normally goes through trackStat() (batched, fire and forget);
  // this is the plain endpoint for callers that want the response.
  reportStats(events: StatEventReport | StatEventReport[]) {
    return useApi.getState().post('api/stats/', Array.isArray(events) ? { events } : events)
  },
  getStatsSummary(filters = undefined) {
    return useApi.getState().get(`api/stats/summary/${statParams(filters)}`)
  },
  getStatsTimeseries(filters = undefined) {
    return useApi.getState().get(`api/stats/timeseries/${statParams(filters)}`)
  },
  getStatsHistory(filters = undefined) {
    return useApi.getState().get(`api/stats/history/${statParams(filters)}`)
  },
}))
